import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { TimeSlotsModalComponent } from '../time-slots-modal/time-slots-modal.component';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-schedule-templates-modal',
  templateUrl: './schedule-templates-modal.component.html',
  styleUrls: ['./schedule-templates-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ScheduleTemplatesModalComponent {
  templates: any[] = [];

  constructor(
    private dialog: MatDialog,
    private backofficeService: BackOfficeService,
    private snackBar: MatSnackBar,
    public translate: TranslocoService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.loadTemplates();
  }

  loadTemplates() {
    this.backofficeService.getScheduleTemplates(this.data.selectedServiceId).subscribe({
      next: (templates) => {
        this.templates = templates || [];
      },
      error: (error) => {
        console.error('Error loading templates:', error);
        this.templates = [];
      }
    });
  }

  getTotalCapacity(slots: any[]): number {
    return slots?.reduce((total, slot) => total + (slot.Capacity || 0), 0) || 0;
  }

  public getTemplateDescription(desc: any): string {
    if (!desc) return '';

    const lang = this.translate.getActiveLang
      ? this.translate.getActiveLang()
      : 'en';

    // Case: object
    if (typeof desc === 'object') {
      const en = desc.en?.trim() || '';
      const sv = desc.sv?.trim() || '';

      if (!en && !sv) return '';

      return desc[lang] || en || sv || '';
    }

    // Case: string
    if (typeof desc === 'string') {
      try {
        const parsed = JSON.parse(desc);

        if (parsed && typeof parsed === 'object') {
          const en = parsed.en?.trim() || '';
          const sv = parsed.sv?.trim() || '';

          if (!en && !sv) return '';

          return parsed[lang] || en || sv || '';
        }
      } catch {
        // not JSON
      }

      return desc;
    }

    return String(desc);
  }

  // Returns a localized label for the template name using active language, falls back to en then sv then raw.
  public getTemplateLabel(name: any): string {
    if (!name) return '';
    const lang = this.translate.getActiveLang ? this.translate.getActiveLang() : 'en';
    if (typeof name === 'object') {
      return name[lang] || name.en || name.sv || JSON.stringify(name);
    }
    if (typeof name === 'string') {
      try {
        const parsed = JSON.parse(name);
        if (parsed && typeof parsed === 'object') {
          return parsed[lang] || parsed.en || parsed.sv || name;
        }
      } catch (e) {
      }
      return name;
    }

    return String(name);
  }

  deleteTemplate(template: any) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Are you sure you want to delete the template?`,
        buttonText: {
          ok: 'Ok',
          cancel: 'Cancel'
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.backofficeService.deleteScheduleTemplate(template.id).subscribe({
          next: () => {
            this.loadTemplates();
            if (this.data.onTemplateUpdate) {
              this.data.onTemplateUpdate();
            }
          },
          error: (error) => {
            this.snackBar.open('Failed to delete template. Please try again.', 'OK', {
              duration: 5000, verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  editTemplate(template: any) {
    const dialogRef = this.dialog.open(TimeSlotsModalComponent, {
      width: '600px',
      data: {
        template: template,
        selectedServiceId: this.data.selectedServiceId,
        organization: this.data.organization
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadTemplates();
        if (this.data.onTemplateUpdate) {
          this.data.onTemplateUpdate();
        }
      }
    });
  }

  addTemplate() {
    const dialogRef = this.dialog.open(TimeSlotsModalComponent, {
      width: '600px',
      data: {
        selectedServiceId: this.data.selectedServiceId,
        organization: this.data.organization
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadTemplates();
        if (this.data.onTemplateUpdate) {
          this.data.onTemplateUpdate();
        }
      }
    });
  }
}